import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  getAdminOverview, getAdminOrders, getAdminLeads, getAdminSubscribers,
  getAdminMessages, getAdminProducts, patchAdminProduct, getCategories,
  uploadProductImage, refundOrder, patchOrderStatus, markOrderPaid, fileUrl,
  getAdminCampaigns, createCampaign, updateCampaign, deleteCampaign,
  formatPrice, formatApiError,
} from "@/lib/api";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { FALLBACK_IMG } from "@/lib/images";
import { toast } from "sonner";
import { ImagePlus, Loader2, Megaphone, Pencil, Plus, RotateCcw, ShieldCheck, Trash2, X } from "lucide-react";

const TABS = [
  { id: "orders", label: "Siparişler" },
  { id: "products", label: "Ürünler" },
  { id: "campaigns", label: "Kampanyalar" },
  { id: "leads", label: "Bayilik Başvuruları" },
  { id: "subscribers", label: "Aboneler" },
  { id: "messages", label: "Mesajlar" },
];

const ORDER_STATUS = {
  paid: "Ödendi", pending: "Ödeme Bekliyor", kapida: "Kapıda Ödeme",
  failed: "Başarısız", expired: "Süresi Doldu", refunded: "İade Edildi",
};
const FULFILLMENT = {
  hazirlaniyor: "Hazırlanıyor", kargoda: "Kargoda", "teslim-edildi": "Teslim Edildi",
  "iade-edildi": "İade Edildi", "havale-bekleniyor": "Havale Bekleniyor", "odeme-bekleniyor": "Ödeme Bekleniyor",
};
const STYLES = ["minimal", "romantik", "isiltili", "gunluk", "davet", "hediye", "trend"];
const OCCASIONS = ["gunluk", "ofis", "aksam", "ozel-gun", "hediye"];

const EMPTY_PRODUCT = {
  name: "", category: "kolyeler", price: "", compare_at: "", short: "", subcategory: "", description: "",
  materials: "", images: "", style: "minimal", occasion: "gunluk",
  isNew: false, isBestseller: false, stockStatus: "out_of_stock", stock_quantity: "0",
  sku: "", variants_text: "", color: "", giftReady: false, tags_text: "",
  gift_for_text: "", gift_moments_text: "", seo_title: "", seo_description: "",
};

const EMPTY_CAMPAIGN = {
  title: "", subtitle: "", cta_label: "Keşfet", cta_link: "/", active: true, show_in_bar: false,
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ProductEditor({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_PRODUCT,
    ...initial,
    images: Array.isArray(initial.images) ? initial.images.join("\n") : (initial.images || ""),
    stock_quantity: String(initial.stock_quantity ?? 0),
    variants_text: Array.isArray(initial.variants)
      ? initial.variants.map((v) => `${v.id || ""}|${v.label || ""}`).join("\n")
      : "",
    tags_text: Array.isArray(initial.tags) ? initial.tags.join(", ") : "",
    gift_for_text: Array.isArray(initial.gift_for) ? initial.gift_for.join(", ") : "",
    gift_moments_text: Array.isArray(initial.gift_moments) ? initial.gift_moments.join(", ") : "",
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editing = !!initial.slug;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full min-h-[44px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const r = await uploadProductImage(f);
        urls.push(fileUrl(r.path));
      }
      set("images", (form.images ? form.images.trimEnd() + "\n" : "") + urls.join("\n"));
      toast.success(`${files.length} görsel yüklendi`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      compare_at: form.compare_at ? parseFloat(form.compare_at) : null,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      stock_quantity: Math.max(0, parseInt(form.stock_quantity || "0", 10) || 0),
      variants: form.variants_text.split("\n").map((line) => {
        const [id, ...labelParts] = line.split("|");
        return { id: (id || "").trim(), label: labelParts.join("|").trim() };
      }).filter((v) => v.id && v.label),
      tags: form.tags_text.split(",").map((s) => s.trim()).filter(Boolean),
      gift_for: form.gift_for_text.split(",").map((s) => s.trim()).filter(Boolean),
      gift_moments: form.gift_moments_text.split(",").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.variants_text;
    delete payload.tags_text;
    delete payload.gift_for_text;
    delete payload.gift_moments_text;
    try {
      if (editing) await api.put(`/admin/products/${initial.slug}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Ürün güncellendi" : "Ürün eklendi");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="product-editor-form" onSubmit={save} className="rounded-2xl border border-shule-gold/40 bg-shule-goldlight/20 p-5 space-y-3 mb-4">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl">{editing ? `Düzenle: ${initial.name}` : "Yeni Ürün"}</p>
        <button type="button" data-testid="product-editor-close" onClick={onClose} className="w-11 h-11 flex items-center justify-center" aria-label="Kapat">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="editor-name" required minLength={2} className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ürün adı" />
        <select data-testid="editor-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input data-testid="editor-price" required type="number" min="1" step="0.01" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Fiyat (TL)" />
        <input data-testid="editor-compare" type="number" min="1" step="0.01" className={inputCls} value={form.compare_at} onChange={(e) => set("compare_at", e.target.value)} placeholder="İndirim öncesi fiyat (opsiyonel)" />
      </div>
      <input data-testid="editor-short" className={inputCls} value={form.short} onChange={(e) => set("short", e.target.value)} placeholder="Kısa açıklama" />
      <input data-testid="editor-subcategory" className={inputCls} value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="Alt kategori (örn. Halka Küpeler)" />
      <textarea data-testid="editor-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
        className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none" placeholder="Ürün açıklaması" />
      <input data-testid="editor-materials" className={inputCls} value={form.materials} onChange={(e) => set("materials", e.target.value)} placeholder="Malzeme (örn. 14 ayar altın kaplama)" />
      <div className="grid sm:grid-cols-3 gap-3">
        <input data-testid="editor-sku" className={inputCls} value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="SKU / stok kodu" />
        <input data-testid="editor-stock-quantity" type="number" min="0" step="1" className={inputCls} value={form.stock_quantity} onChange={(e) => set("stock_quantity", e.target.value)} placeholder="Stok adedi" />
        <input data-testid="editor-color" className={inputCls} value={form.color} onChange={(e) => set("color", e.target.value)} placeholder="Renk (varsa)" />
      </div>
      <textarea data-testid="editor-variants" rows={2} value={form.variants_text} onChange={(e) => set("variants_text", e.target.value)}
        className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none font-mono text-xs"
        placeholder={"Varyantlar — her satır: kod|etiket\nÖrn: gold|Gold"} />
      <div className="grid sm:grid-cols-3 gap-3">
        <input data-testid="editor-tags" className={inputCls} value={form.tags_text} onChange={(e) => set("tags_text", e.target.value)} placeholder="Etiketler (virgülle)" />
        <input data-testid="editor-gift-for" className={inputCls} value={form.gift_for_text} onChange={(e) => set("gift_for_text", e.target.value)} placeholder="Hediye kime (virgülle)" />
        <input data-testid="editor-gift-moments" className={inputCls} value={form.gift_moments_text} onChange={(e) => set("gift_moments_text", e.target.value)} placeholder="Hediye anları (virgülle)" />
      </div>
      <input data-testid="editor-seo-title" maxLength={160} className={inputCls} value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="SEO başlığı (opsiyonel)" />
      <textarea data-testid="editor-seo-description" maxLength={320} rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)}
        className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none"
        placeholder="SEO açıklaması (opsiyonel)" />
      <textarea data-testid="editor-images" rows={2} value={form.images} onChange={(e) => set("images", e.target.value)}
        className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none font-mono text-xs"
        placeholder="Görsel URL'leri (her satıra bir tane)" />
      <label data-testid="editor-upload-label"
        className={`flex items-center justify-center gap-2 min-h-[48px] rounded-2xl border border-dashed border-shule-gold/60 text-sm font-semibold cursor-pointer transition-colors hover:bg-shule-goldlight/30 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-shule-goldhover" />}
        {uploading ? "Yükleniyor…" : "Cihazdan görsel yükle (en fazla 5 MB)"}
        <input data-testid="editor-file-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select data-testid="editor-style" className={inputCls} value={form.style} onChange={(e) => set("style", e.target.value)}>
          {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="editor-occasion" className={inputCls} value={form.occasion} onChange={(e) => set("occasion", e.target.value)}>
          {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select data-testid="editor-stock" className={inputCls} value={form.stockStatus} onChange={(e) => set("stockStatus", e.target.value)}>
          <option value="in_stock">Stokta</option>
          <option value="out_of_stock">Stok Dışı</option>
        </select>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer min-h-[44px]">
            <input data-testid="editor-isnew" type="checkbox" checked={form.isNew} onChange={(e) => set("isNew", e.target.checked)} className="w-5 h-5 accent-shule-gold" /> Yeni
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer min-h-[44px]">
            <input data-testid="editor-isbestseller" type="checkbox" checked={form.isBestseller} onChange={(e) => set("isBestseller", e.target.checked)} className="w-5 h-5 accent-shule-gold" /> Çok Satan
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer min-h-[44px]">
            <input data-testid="editor-gift-ready" type="checkbox" checked={form.giftReady} onChange={(e) => set("giftReady", e.target.checked)} className="w-5 h-5 accent-shule-gold" /> Hediye uygun
          </label>
        </div>
      </div>
      <button data-testid="editor-save" disabled={saving} className="btn-primary-shule w-full disabled:opacity-40">
        {saving ? "Kaydediliyor…" : editing ? "Değişiklikleri Kaydet" : "Ürünü Ekle"}
      </button>
    </form>
  );
}

function CampaignEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const editing = !!initial.slug;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full min-h-[44px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await updateCampaign(initial.slug, form);
      else await createCampaign(form);
      toast.success(editing ? "Kampanya güncellendi" : "Kampanya eklendi");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="campaign-editor-form" onSubmit={save} className="rounded-2xl border border-shule-gold/40 bg-shule-goldlight/20 p-5 space-y-3 mb-4">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl">{editing ? `Düzenle: ${initial.title}` : "Yeni Kampanya"}</p>
        <button type="button" data-testid="campaign-editor-close" onClick={onClose} className="w-11 h-11 flex items-center justify-center" aria-label="Kapat">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input data-testid="campaign-title" required minLength={2} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Kampanya fikri (başlık — oran değil)" />
      <input data-testid="campaign-subtitle" className={inputCls} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Alt metin (oran/ayrıcalık burada ikincil kalır)" />
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="campaign-cta-label" className={inputCls} value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Buton metni" />
        <input data-testid="campaign-cta-link" className={inputCls} value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="Buton linki (örn. /hediye)" />
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer min-h-[44px]">
          <input data-testid="campaign-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-shule-gold" />
          Yayında
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer min-h-[44px]">
          <input data-testid="campaign-show-in-bar" type="checkbox" checked={form.show_in_bar} onChange={(e) => set("show_in_bar", e.target.checked)} className="w-5 h-5 accent-shule-gold" />
          Duyuru çubuğunda göster
        </label>
      </div>
      <button data-testid="campaign-save" disabled={saving} className="btn-primary-shule w-full disabled:opacity-40">
        {saving ? "Kaydediliyor…" : editing ? "Değişiklikleri Kaydet" : "Kampanyayı Ekle"}
      </button>
    </form>
  );
}

function CampaignRow({ c, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await updateCampaign(c.slug, {
        title: c.title, subtitle: c.subtitle || "", cta_label: c.cta_label || "Keşfet",
        cta_link: c.cta_link || "/", active: !c.active, show_in_bar: !!c.show_in_bar,
      });
      toast.success(`${c.title}: ${!c.active ? "yayında" : "yayından kaldırıldı"}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteCampaign(c.slug);
      toast.success("Kampanya silindi");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`admin-campaign-${c.slug}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="min-w-0">
          <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
            <Megaphone className="w-4 h-4 text-shule-gold shrink-0" />
            {c.title}
            {c.active !== false && <span className="badge-tag !bg-shule-goldlight/70">Yayında</span>}
            {c.show_in_bar && <span className="badge-tag">Duyuru Çubuğu</span>}
          </p>
          {c.subtitle && <p className="text-xs text-shule-mocha mt-1">{c.subtitle}</p>}
          <p className="text-[11px] text-shule-soft mt-0.5">{c.cta_label} → {c.cta_link}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`campaign-toggle-${c.slug}`} onClick={toggleActive} disabled={busy}
            className={`min-h-[44px] px-4 rounded-full border text-xs font-semibold transition-colors disabled:opacity-50 ${c.active !== false ? "border-shule-gold bg-shule-goldlight/40" : "border-shule-lineaccent text-shule-soft"}`}>
            {c.active !== false ? "Yayında" : "Pasif"}
          </button>
          <button data-testid={`campaign-edit-${c.slug}`} onClick={() => onEdit(c)}
            className="min-w-[44px] min-h-[44px] rounded-full border border-shule-lineaccent flex items-center justify-center hover:border-shule-gold transition-colors" aria-label="Düzenle">
            <Pencil className="w-4 h-4" />
          </button>
          <button data-testid={`campaign-delete-${c.slug}`} onClick={remove} disabled={busy}
            className="min-w-[44px] min-h-[44px] rounded-full border border-shule-lineaccent flex items-center justify-center text-shule-soft hover:text-red-800 hover:border-red-300 transition-colors" aria-label="Sil">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </li>
  );
}

function OrderRow({ o, onChanged }) {
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [busy, setBusy] = useState(false);

  const doRefund = async () => {
    setBusy(true);
    try {
      const r = await refundOrder(o.session_id);
      toast.success(r.simulated
        ? `#${o.order_id} iade olarak işlendi (test ortamı simülasyonu)`
        : `#${o.order_id} iade edildi`);
      setConfirmRefund(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (val) => {
    try {
      await patchOrderStatus(o.session_id, val);
      toast.success(`#${o.order_id} → ${FULFILLMENT[val]}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await markOrderPaid(o.session_id);
      toast.success(`#${o.order_id} ödendi olarak işlendi`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`admin-order-${o.order_id}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <p className="text-sm font-bold">#{o.order_id} <span className="font-normal text-shule-soft">· {o.user_email || "misafir"}</span></p>
          <p className="text-xs text-shule-mocha mt-1">{o.items?.map((i) => `${i.quantity} × ${i.name || i.slug}`).join(", ")}</p>
          <p className="text-xs text-shule-soft mt-0.5">{fmtDate(o.created_at)}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-xl">{formatPrice(o.amount)}</p>
          <span className="badge-tag">{ORDER_STATUS[o.payment_status] || o.payment_status}</span>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {["paid", "kapida"].includes(o.payment_status) && (
          <select data-testid={`order-status-select-${o.order_id}`} value={o.fulfillment_status || "hazirlaniyor"}
            onChange={(e) => changeStatus(e.target.value)}
            className="min-h-[44px] rounded-full border border-shule-lineaccent bg-shule-base px-4 text-xs font-semibold outline-none focus:border-shule-gold">
            {["hazirlaniyor", "kargoda", "teslim-edildi"].map((s) => (
              <option key={s} value={s}>{FULFILLMENT[s]}</option>
            ))}
          </select>
        )}
        {o.payment_method === "havale" && o.payment_status === "pending" && (
          <button data-testid={`order-mark-paid-${o.order_id}`} onClick={markPaid} disabled={busy}
            className="min-h-[44px] px-4 rounded-full bg-shule-coffee text-shule-base text-xs font-semibold disabled:opacity-50">
            {busy ? "İşleniyor…" : "Havale Ulaştı — Ödendi İşaretle"}
          </button>
        )}
        {o.payment_status === "paid" && (
          <>
            {!confirmRefund ? (
              <button data-testid={`order-refund-${o.order_id}`} onClick={() => setConfirmRefund(true)}
                className="min-h-[44px] px-4 rounded-full border border-shule-lineaccent text-xs font-semibold text-shule-mocha hover:border-shule-gold inline-flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> İade Et
              </button>
            ) : (
              <span className="inline-flex items-center gap-2">
                <button data-testid={`order-refund-confirm-${o.order_id}`} onClick={doRefund} disabled={busy}
                  className="min-h-[44px] px-4 rounded-full bg-red-800 text-white text-xs font-semibold disabled:opacity-50">
                  {busy ? "İşleniyor…" : "İadeyi Onayla"}
                </button>
                <button data-testid={`order-refund-cancel-${o.order_id}`} onClick={() => setConfirmRefund(false)}
                  className="min-h-[44px] px-4 rounded-full border border-shule-lineaccent text-xs font-semibold">
                  Vazgeç
                </button>
              </span>
            )}
          </>
        )}
        {o.fulfillment_status && !["paid", "kapida"].includes(o.payment_status) && (
          <span className="badge-tag">{FULFILLMENT[o.fulfillment_status] || o.fulfillment_status}</span>
        )}
      </div>
    </li>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [overview, setOverview] = useState(null);
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [campaignEditor, setCampaignEditor] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      getAdminOverview().then(setOverview).catch(() => {});
      getCategories().then(setCategories).catch(() => {});
    }
  }, [user]);

  const loadTab = () => {
    setLoading(true);
    const fetchers = {
      orders: getAdminOrders, products: getAdminProducts, campaigns: getAdminCampaigns,
      leads: getAdminLeads, subscribers: getAdminSubscribers, messages: getAdminMessages,
    };
    fetchers[tab]().then(setData).catch(() => setData([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    setEditor(null);
    setCampaignEditor(null);
    loadTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  if (user === null) {
    return <div className="container-shule py-24 text-center text-shule-soft font-serif text-2xl">Yükleniyor…</div>;
  }
  if (user === false) return <Navigate to="/giris" replace />;
  if (user.role !== "admin") {
    return (
      <div data-testid="admin-forbidden" className="container-shule py-24 text-center">
        <ShieldCheck className="w-12 h-12 mx-auto text-shule-taupe" />
        <p className="mt-4 font-serif text-2xl text-shule-mocha">Bu alan yalnızca yöneticilere açık</p>
        <Link to="/" className="btn-outline-shule mt-6">Ana Sayfaya Dön</Link>
      </div>
    );
  }

  const toggleStock = async (p) => {
    const next = p.stockStatus === "in_stock" ? "out_of_stock" : "in_stock";
    try {
      const updated = await patchAdminProduct(p.slug, { stockStatus: next });
      setData((prev) => prev.map((x) => (x.slug === p.slug ? updated : x)));
      toast.success(`${p.name}: ${next === "in_stock" ? "stokta" : "stok dışı"}`);
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const openEdit = (p) => setEditor({
    slug: p.slug, name: p.name, category: p.category, price: String(p.price),
    compare_at: p.compare_at ? String(p.compare_at) : "", short: p.short || "",
    subcategory: p.subcategory || "",
    description: p.description || "", materials: p.materials || "",
    images: (p.images || []).join("\n"), style: p.style || "minimal",
    occasion: p.occasion || "gunluk", isNew: !!p.isNew, isBestseller: !!p.isBestseller,
    stockStatus: p.stockStatus || "in_stock",
  });

  const openCampaignEdit = (c) => setCampaignEditor({
    slug: c.slug, title: c.title, subtitle: c.subtitle || "", cta_label: c.cta_label || "Keşfet",
    cta_link: c.cta_link || "/", active: c.active !== false, show_in_bar: !!c.show_in_bar,
  });

  const overviewItems = overview ? [
    { label: "Ürün", value: overview.products },
    { label: "Sipariş", value: overview.orders },
    { label: "Ödenen", value: overview.paid_orders },
    { label: "Başvuru", value: overview.leads },
    { label: "Abone", value: overview.subscribers },
    { label: "Mesaj", value: overview.messages },
    { label: "Üye", value: overview.users },
  ] : [];

  return (
    <>
      <Seo title="Yönetim Paneli" path="/admin"  noindex />
      <div className="container-shule py-10 lg:py-14">
        <Reveal>
          <p className="eyebrow">Yönetim</p>
          <h1 data-testid="admin-title" className="mt-2 font-serif text-4xl lg:text-5xl text-shule-coffee">Shule Paneli</h1>
        </Reveal>

        {overview && (
          <div data-testid="admin-overview" className="mt-8 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {overviewItems.map((o) => (
              <div key={o.label} className="rounded-2xl border border-shule-line bg-shule-paper p-4 text-center">
                <p className="font-serif text-3xl text-shule-goldhover">{o.value}</p>
                <p className="text-xs text-shule-soft mt-1">{o.label}</p>
              </div>
            ))}
          </div>
        )}

        <div data-testid="admin-tabs" className="mt-8 flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`min-h-[44px] px-5 rounded-full border text-sm font-semibold transition-colors ${tab === t.id ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent hover:border-shule-gold"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "products" && (
            <div className="mb-4">
              {!editor ? (
                <button data-testid="product-add-button" onClick={() => setEditor(EMPTY_PRODUCT)} className="btn-primary-shule !px-6">
                  <Plus className="w-4 h-4" /> Yeni Ürün Ekle
                </button>
              ) : (
                <ProductEditor initial={editor} categories={categories} onClose={() => setEditor(null)}
                  onSaved={() => { setEditor(null); loadTab(); getAdminOverview().then(setOverview).catch(() => {}); }} />
              )}
            </div>
          )}
          {tab === "campaigns" && (
            <div className="mb-4">
              {!campaignEditor ? (
                <button data-testid="campaign-add-button" onClick={() => setCampaignEditor(EMPTY_CAMPAIGN)} className="btn-primary-shule !px-6">
                  <Plus className="w-4 h-4" /> Yeni Kampanya Ekle
                </button>
              ) : (
                <CampaignEditor initial={campaignEditor} onClose={() => setCampaignEditor(null)}
                  onSaved={() => { setCampaignEditor(null); loadTab(); }} />
              )}
            </div>
          )}
          {loading || data === null ? (
            <p className="text-sm text-shule-soft py-10">Yükleniyor…</p>
          ) : data.length === 0 ? (
            <p data-testid="admin-empty" className="text-sm text-shule-soft py-10">Kayıt yok.</p>
          ) : (
            <ul className="space-y-3">
              {tab === "orders" && data.map((o) => (
                <OrderRow key={o.session_id} o={o} onChanged={() => { loadTab(); getAdminOverview().then(setOverview).catch(() => {}); }} />
              ))}
              {tab === "products" && data.map((p) => (
                <li key={p.slug} data-testid={`admin-product-${p.slug}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4 flex gap-4 items-center">
                  <img src={p.images?.[0] || FALLBACK_IMG} alt="" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    className="w-14 h-16 rounded-xl object-cover border border-shule-line" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{p.name}</p>
                    <p className="text-xs text-shule-soft">{p.sku} · {p.category_name}</p>
                  </div>
                  <p className="font-bold text-sm">{formatPrice(p.price)}</p>
                  <button data-testid={`admin-stock-toggle-${p.slug}`} onClick={() => toggleStock(p)}
                    className={`min-h-[44px] px-4 rounded-full border text-xs font-semibold transition-colors ${p.stockStatus === "in_stock" ? "border-shule-gold bg-shule-goldlight/40" : "border-shule-lineaccent text-shule-soft"}`}>
                    {p.stockStatus === "in_stock" ? "Stokta" : "Stok Dışı"}
                  </button>
                  <button data-testid={`admin-edit-${p.slug}`} onClick={() => openEdit(p)}
                    className="min-w-[44px] min-h-[44px] rounded-full border border-shule-lineaccent flex items-center justify-center hover:border-shule-gold transition-colors" aria-label="Düzenle">
                    <Pencil className="w-4 h-4" />
                  </button>
                </li>
              ))}
              {tab === "campaigns" && data.map((c) => (
                <CampaignRow key={c.slug} c={c} onEdit={openCampaignEdit} onChanged={loadTab} />
              ))}
              {tab === "leads" && data.map((l, i) => (
                <li key={i} data-testid={`admin-lead-${i}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4">
                  <p className="font-semibold text-sm">{l.name} <span className="font-normal text-shule-soft">· {l.city} · {l.investment_model === "kiosk" ? "Kiosk" : "Mağaza"}</span></p>
                  <p className="text-xs text-shule-mocha mt-1">{l.email} · {l.phone}{l.budget ? ` · Bütçe: ${l.budget}` : ""}</p>
                  {l.message && <p className="text-xs text-shule-soft mt-1 italic">"{l.message}"</p>}
                  <p className="text-[11px] text-shule-soft mt-1">{fmtDate(l.created_at)}</p>
                </li>
              ))}
              {tab === "subscribers" && data.map((s, i) => (
                <li key={i} data-testid={`admin-subscriber-${i}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4 flex justify-between items-center">
                  <p className="text-sm font-semibold">{s.email}</p>
                  <span className="badge-tag">{s.source === "club" ? "Shule Club" : "Bülten"}</span>
                </li>
              ))}
              {tab === "messages" && data.map((m, i) => (
                <li key={i} data-testid={`admin-message-${i}`} className="rounded-2xl border border-shule-line bg-shule-paper p-4">
                  <p className="font-semibold text-sm">{m.subject} <span className="font-normal text-shule-soft">· {m.name} ({m.email})</span></p>
                  <p className="text-sm text-shule-mocha mt-1">{m.message}</p>
                  <p className="text-[11px] text-shule-soft mt-1">{fmtDate(m.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
